import { useCallback, useEffect, useRef, useState } from 'react';

export interface Attachment {
  file: File;
  id: string;
}

const SIZE_LIMITS = {
  IMAGE: 20,
  DOCUMENT: 100,
} as const;

const getLimit = (type: string): number => {
  if (type.startsWith('image/')) return SIZE_LIMITS.IMAGE;
  return SIZE_LIMITS.DOCUMENT;
};

const getValidationError = (f: File): string | null => {
  const limit = getLimit(f.type) * 1024 * 1024;
  if (f.size > limit) {
    return `Máximo ${limit / 1024 / 1024} MB`;
  }
  const okMime =
    f.type.startsWith('image/') ||
    f.type === 'application/pdf' ||
    f.type === 'application/vnd.ms-excel' ||
    f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (!okMime) {
    return 'Solo se permiten imágenes (jpg, png, gif), PDF y archivos Excel';
  }
  return null;
};

interface UseFileAttachmentsReturn {
  attachments: Attachment[];
  isDragActive: boolean;
  isInvalid: boolean;
  error: string | null;
  onDragEnter: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  remove: (id: string) => void;
  clear: () => void;
}

const useFileAttachments = (maxFiles = 1): UseFileAttachmentsReturn => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dragCounter = useRef(0);
  const idCounter = useRef(0);

  const validate = useCallback((f: File): boolean => {
    const err = getValidationError(f);
    setError(err);
    return err === null;
  }, []);

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) return;

      const validFiles: File[] = [];
      let firstValidationError: string | null = null;
      incoming.forEach((f) => {
        const err = getValidationError(f);
        if (err) {
          if (!firstValidationError) firstValidationError = err;
        } else {
          validFiles.push(f);
        }
      });

      setAttachments((prev) => {
        if (maxFiles === 1) {
          if (validFiles.length === 0) {
            setError(firstValidationError);
            return prev;
          }
          setError(null);
          const id = `${validFiles[0].name}-${Date.now()}-${idCounter.current}`;
          idCounter.current += 1;
          return [{ file: validFiles[0], id }];
        }

        const remaining = Math.max(0, maxFiles - prev.length);
        let finalError = firstValidationError;
        if (validFiles.length > remaining) {
          finalError = `Solo puedes adjuntar hasta ${maxFiles} archivos`;
        }
        setError(finalError);

        if (remaining === 0) return prev;
        const now = Date.now();
        const accepted = validFiles.slice(0, remaining).map((file) => {
          const id = `${file.name}-${now}-${idCounter.current}`;
          idCounter.current += 1;
          return { file, id };
        });
        if (accepted.length === 0) return prev;
        return [...prev, ...accepted];
      });
    },
    [maxFiles]
  );

  const onDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current += 1;
      setIsDragActive(true);
      const f = e.dataTransfer.items[0]?.getAsFile();
      setIsInvalid(f ? !validate(f) : false);
    },
    [validate]
  );

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const f = e.dataTransfer.items[0]?.getAsFile();
      setIsInvalid(f ? !validate(f) : false);
    },
    [validate]
  );

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragActive(false);
      setIsInvalid(false);
      setError(null);
    }
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
      setIsInvalid(false);
      dragCounter.current = 0;
      const files = Array.from(e.dataTransfer.files ?? []);
      if (files.length) addFiles(files);
    },
    [addFiles]
  );

  useEffect(() => {
    const ov = (e: DragEvent) => e.preventDefault();
    window.addEventListener('dragover', ov);
    window.addEventListener('drop', ov);
    return () => {
      window.removeEventListener('dragover', ov);
      window.removeEventListener('drop', ov);
    };
  }, []);

  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (files.length) addFiles(files);
      e.target.value = '';
    },
    [addFiles]
  );

  const remove = useCallback((id: string) => {
    setAttachments((a) => a.filter((x) => x.id !== id));
  }, []);

  const clear = useCallback(() => {
    setAttachments([]);
    setError(null);
  }, []);

  return {
    attachments,
    isDragActive,
    isInvalid,
    error,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    onFileChange,
    remove,
    clear,
  };
};

export default useFileAttachments;
